/*
 * Checkout Progress Context
 * Provides the current header-level checkout step (Shipping / Payment / Review)
 * to components rendered outside the CheckoutProvider (e.g. CheckoutHeader).
 *
 * The CheckoutProvider (in checkout-context.js) is responsible for:
 *   1. Syncing headerStep whenever the internal step changes.
 *   2. Injecting a `navigateToHeaderStep` callback so the progress bar can
 *      trigger navigation without directly depending on CheckoutProvider.
 */
import React, {createContext, useContext, useState, useCallback} from 'react'
import PropTypes from 'prop-types'

// Header-level step indices
export const HEADER_STEP = {
    SHIPPING: 0,
    PAYMENT: 1,
    REVIEW: 2
}

const CheckoutProgressContext = createContext({
    headerStep: HEADER_STEP.SHIPPING,
    setHeaderStep: () => {},
    // navigateToHeaderStep is set by CheckoutProvider once it mounts
    navigateToHeaderStep: null,
    setNavigateToHeaderStep: () => {},
    // Tracks whether the user is on the second sub-step (shipping options) within the Shipping header step
    shippingSubStep: 0,
    setShippingSubStep: () => {},
    // Global checkout error surfaced by checkout/index.jsx, consumed by CheckoutHeader
    checkoutError: null,
    setCheckoutError: () => {},
    // Tracks whether the mobile OrderSummary drawer is open so the header can
    // hide its duplicate error callout while the drawer (which renders its own) is visible
    isOrderSummaryDrawerOpen: false,
    setIsOrderSummaryDrawerOpen: () => {}
})

export const CheckoutProgressProvider = ({children}) => {
    const [headerStep, setHeaderStep] = useState(HEADER_STEP.SHIPPING)
    // navigateToHeaderStep is injected by CheckoutProvider after it mounts
    const [navigateToHeaderStep, _setNavigateToHeaderStep] = useState(null)
    const [shippingSubStep, setShippingSubStep] = useState(0)
    const [checkoutError, setCheckoutError] = useState(null)
    const [isOrderSummaryDrawerOpen, setIsOrderSummaryDrawerOpen] = useState(false)

    // Stable setter that avoids the "value is a function" useState pitfall
    const setNavigateToHeaderStep = useCallback((fn) => {
        _setNavigateToHeaderStep(() => fn)
    }, [])

    return (
        <CheckoutProgressContext.Provider
            value={{
                headerStep,
                setHeaderStep,
                navigateToHeaderStep,
                setNavigateToHeaderStep,
                shippingSubStep,
                setShippingSubStep,
                checkoutError,
                setCheckoutError,
                isOrderSummaryDrawerOpen,
                setIsOrderSummaryDrawerOpen
            }}
        >
            {children}
        </CheckoutProgressContext.Provider>
    )
}

CheckoutProgressProvider.propTypes = {
    children: PropTypes.any
}

export const useCheckoutProgress = () => useContext(CheckoutProgressContext)
